(() => {
class WfFeaturedProjects extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.carousel = this.querySelector('effect-carousel');
    this.slides = Array.from(this.querySelectorAll('.wf-home-featured-project__project'));
    this.currentOutput = this.querySelector('[data-featured-project-current]');
    this.previousButton = this.querySelector('[data-featured-project-previous]');
    this.nextButton = this.querySelector('[data-featured-project-next]');
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.autoplayInterval = Math.max(3, Number(this.dataset.autoplayInterval) || 6) * 1000;
    this.isHovered = false;
    this.hasFocus = false;

    if (!this.carousel || this.slides.length === 0) return;

    const signal = this.abortController.signal;

    this.carousel.addEventListener('carousel:change', this.handleCarouselChange, { signal });
    this.carousel.addEventListener('swipeleft', this.handleReducedMotionSwipe, { capture: true, signal });
    this.carousel.addEventListener('swiperight', this.handleReducedMotionSwipe, { capture: true, signal });
    this.previousButton?.addEventListener('click', this.showPreviousProject, { signal });
    this.nextButton?.addEventListener('click', this.showNextProject, { signal });
    this.addEventListener('mouseenter', this.handleMouseEnter, { signal });
    this.addEventListener('mouseleave', this.handleMouseLeave, { signal });
    this.addEventListener('focusin', this.handleFocusIn, { signal });
    this.addEventListener('focusout', this.handleFocusOut, { signal });
    document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
    this.motionPreference.addEventListener('change', this.handleMotionPreferenceChange, { signal });

    const initialIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-selected')));
    this.updateSlideState(initialIndex);
    this.scheduleAutoplay();
  }

  disconnectedCallback() {
    this.clearAutoplay();
    this.abortController?.abort();
    this.abortController = null;
  }

  handleCarouselChange = (event) => {
    this.updateSlideState(event.detail.index);
    this.scheduleAutoplay();
  };

  handleReducedMotionSwipe = (event) => {
    if (!this.motionPreference.matches) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  showPreviousProject = () => {
    this.carousel.previous?.(!this.motionPreference.matches);
    this.scheduleAutoplay();
  };

  showNextProject = () => {
    this.carousel.next?.(!this.motionPreference.matches);
    this.scheduleAutoplay();
  };

  handleMouseEnter = () => {
    this.isHovered = true;
    this.clearAutoplay();
  };

  handleMouseLeave = () => {
    this.isHovered = false;
    this.scheduleAutoplay();
  };

  handleFocusIn = () => {
    this.hasFocus = true;
    this.clearAutoplay();
  };

  handleFocusOut = () => {
    window.requestAnimationFrame(() => {
      this.hasFocus = this.contains(document.activeElement);
      this.scheduleAutoplay();
    });
  };

  handleVisibilityChange = () => {
    this.scheduleAutoplay();
  };

  handleMotionPreferenceChange = () => {
    this.scheduleAutoplay();
  };

  updateSlideState(index) {
    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;

      slide.setAttribute('aria-hidden', String(!isActive));
      slide.inert = !isActive;
    });

    if (this.currentOutput) {
      this.currentOutput.textContent = String(index + 1).padStart(2, '0');
    }
  }

  canAutoplay() {
    return this.autoplayEnabled
      && !this.motionPreference.matches
      && !this.isHovered
      && !this.hasFocus
      && document.visibilityState === 'visible';
  }

  scheduleAutoplay() {
    this.clearAutoplay();

    if (!this.canAutoplay()) return;

    this.autoplayTimer = window.setTimeout(() => {
      this.carousel.next?.(true);
    }, this.autoplayInterval);
  }

  clearAutoplay() {
    if (!this.autoplayTimer) return;

    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }
}

if (!customElements.get('wf-featured-projects')) {
  customElements.define('wf-featured-projects', WfFeaturedProjects);
}
})();
